import { gql } from '@apollo/client'

// Queries
export const GET_CHAT_STATS = gql`
  query GetChatStats {
    getChatStats {
      totalConversations
      totalMessages
      lastActivityAt
    }
  }
`

export const GET_USER_CONVERSATIONS = gql`
  query GetUserConversations($limit: Float) {
    getUserConversations(limit: $limit) {
      id
      userId
      messages {
        id
        conversationId
        userId
        role
        content
        modelId
        timestamp
        toolCalls {
          tool
          args
          result
          position
        }
        reasoning {
          content
          position
        }
        uiComponent {
          type
          data
          actions {
            label
            action
            params
          }
        }
        attachments {
          filename
          mimetype
          size
          url
        }
      }
      isVoiceConversation
      createdAt
      updatedAt
      lastModelUsed
      isGenerating
    }
  }
`

export const GET_CHAT_HISTORY = gql`
  query GetChatHistory($conversationId: String!) {
    getChatHistory(conversationId: $conversationId) {
      id
      conversationId
      userId
      role
      content
      modelId
      timestamp
      toolCalls {
        tool
        args
        result
        position
        positionType
      }
      reasoning {
        content
        position
      }
      uiComponent {
        type
        data
        actions {
          label
          action
          params
        }
      }
      attachments {
        filename
        mimetype
        size
        url
      }
    }
  }
`

export const GET_AVAILABLE_MODELS = gql`
  query GetAvailableModels {
    getAvailableModels {
      id
      name
      provider
      description
      contextLength
    }
  }
`

// Mutations
export const INIT_CHAT = gql`
  mutation InitChat($input: InitChatInput!) {
    initChat(input: $input) {
      id
      userId
      lastModelUsed
      createdAt
      updatedAt
    }
  }
`

export const SEND_CHAT_MESSAGE = gql`
  mutation SendChatMessage($input: SendMessageInput!) {
    sendChatMessage(input: $input) {
      id
      conversationId
      userId
      role
      content
      modelId
      timestamp
      toolCalls {
        tool
        args
        result
        position
        positionType
      }
      reasoning {
        content
        position
      }
      uiComponent {
        type
        data
        actions {
          label
          action
          params
        }
      }
      attachments {
        id
        filename
        mimetype
        size
        url
      }
    }
  }
`

export const UPDATE_MESSAGE_UI_COMPONENT = gql`
  mutation UpdateMessageUIComponent($input: UpdateMessageUIComponentInput!) {
    updateMessageUIComponent(input: $input) {
      id
      conversationId
      userId
      role
      content
      modelId
      timestamp
      uiComponent {
        type
        data
        actions {
          label
          action
          params
        }
      }
    }
  }
`

export const END_CONVERSATION = gql`
  mutation EndConversation($conversationId: String!) {
    endConversation(conversationId: $conversationId)
  }
`

// Subscriptions
export const ON_CHAT_MESSAGE = gql`
  subscription OnChatMessage {
    onChatMessage {
      id
      conversationId
      userId
      role
      content
      modelId
      timestamp
      toolCalls {
        tool
        args
        result
        position
        positionType
      }
      reasoning {
        content
        position
      }
      uiComponent {
        type
        data
        actions {
          label
          action
          params
        }
      }
      attachments {
        id
        filename
        mimetype
        size
        url
      }
    }
  }
`

export const ON_TYPING_INDICATOR = gql`
  subscription OnTypingIndicator {
    onTypingIndicator {
      userId
      conversationId
      isTyping
    }
  }
`

export const ON_TOOL_EXECUTION = gql`
  subscription OnToolExecution {
    onToolExecution {
      tool
      args
      result
    }
  }
`

export const ON_STREAMING_MESSAGE = gql`
  subscription OnStreamingMessage {
    onStreamingMessage {
      conversationId
      messageId
      chunk
      isComplete
      offset
      uiComponent {
        type
        data
        actions {
          label
          action
          params
        }
      }
    }
  }
`

export const ON_TOOL_CALL_EVENT = gql`
  subscription OnToolCallEvent {
    onToolCallEvent {
      conversationId
      messageId
      toolName
      status
      args
      result
      position
      positionType
    }
  }
`

export const ON_REASONING_EVENT = gql`
  subscription OnReasoningEvent {
    onReasoningEvent {
      conversationId
      messageId
      status
      content
      position
      blockId
    }
  }
`

export const ON_NEW_CONVERSATION = gql`
  subscription OnNewConversation {
    onNewConversation {
      id
      userId
      isVoiceConversation
      createdAt
      updatedAt
      lastModelUsed
    }
  }
`

export const STOP_CHAT_GENERATION = gql`
  mutation StopChatGeneration($conversationId: String!) {
    stopChatGeneration(conversationId: $conversationId)
  }
`

// A reply the assistant is still writing, with what it has so far. The chat
// page picks it up when it comes back mid-reply.
export const GET_ACTIVE_GENERATION = gql`
  query GetActiveGeneration($conversationId: String!) {
    getActiveGeneration(conversationId: $conversationId) {
      conversationId
      messageId
      startedAt
      phase
      content
      contentLength
      toolCalls {
        tool
        args
        result
        error
        status
        position
        positionType
      }
      reasoning {
        blockId
        content
        position
        done
      }
      uiComponent {
        type
        data
        actions {
          label
          action
          params
        }
      }
    }
  }
`

// Which conversations have a reply being written right now: the menu's
// working marker reads these, and they update live over the subscription.
export const GET_ACTIVE_CHAT_GENERATIONS = gql`
  query GetActiveChatGenerations {
    activeChatGenerations {
      conversationId
      messageId
      status
      startedAt
    }
  }
`

export const ON_CHAT_GENERATION_STATUS = gql`
  subscription OnChatGenerationStatus {
    onChatGenerationStatus {
      conversationId
      messageId
      status
      startedAt
    }
  }
`
